import { Link } from "react-router-dom";
import styled from "styled-components";

import NavBar from "./NavBar";
import Footer from "./Footer";
import { BRANDS, brandForHost } from "../../data/brand";
import { currentSession } from "../../data/accountApi";

/*
 * Header and footer that follow the hostname.
 *
 * The house price pages moved to doorstepdata.co.uk but are still served by
 * the same build from the same box, so without this they would wear the
 * agency's navigation on the product's domain - "PROPERTY PEOPLE" above a page
 * that is deliberately not Property People, with a menu of lettings and sales
 * links that do not exist on that site.
 *
 * On property-people.co.uk these render the agency chrome exactly as before,
 * so the components are a no-op anywhere the brand has not changed.
 *
 * The product's chrome is deliberately plain, and for its first few days it
 * carried no navigation at all - there was nowhere to go, the root being a
 * 302 to the report tool. Now that there is a homepage the mark goes home and
 * the bar carries the things the site actually has. A menu padded out to look
 * like a real site is how a two page product starts advertising pages it does
 * not have, so an entry goes in when the page exists and answers a question
 * somebody is actually asking - which is what /pricing did on 5 November
 * 2026, and why the bar is three wide rather than two.
 */

const Bar = styled.header`
    background: #0f4c5c;
    color: #fff;
    padding: 0.9rem 0;
`;
const Inner = styled.div`
    max-width: 75rem;
    margin: 0 auto;
    padding: 0 1rem;
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    flex-wrap: wrap;
`;
const Mark = styled(Link)`
    color: #fff;
    font-weight: 700;
    font-size: 1.1rem;
    letter-spacing: 0.01em;
    text-decoration: none;
    &:hover {
        color: #fff;
    }
`;
const Tag = styled.span`
    font-size: 0.78rem;
    opacity: 0.75;
`;
/* Pushed right where there is room, and allowed to drop onto its own line
   below that rather than squeezing the tagline. */
const Nav = styled.nav`
    margin-left: auto;
    display: flex;
    gap: 1.1rem;
    font-size: 0.88rem;
    a {
        color: #cfe6ec;
        text-decoration: none;
        &:hover {
            color: #fff;
            text-decoration: underline;
        }
    }
    @media (max-width: 560px) {
        margin-left: 0;
        flex-basis: 100%;
    }
`;

const FootWrap = styled.footer`
    background: #0b3b47;
    color: #fff;
    margin-top: 3rem;
    padding: 1.75rem 0;
    font-size: 0.85rem;
    line-height: 1.6;
`;
const FootInner = styled.div`
    max-width: 75rem;
    margin: 0 auto;
    padding: 0 1rem;
    display: grid;
    gap: 1.25rem;
    grid-template-columns: repeat(auto-fit, minmax(min(18rem, 100%), 1fr));
    a {
        color: #cfe6ec;
    }
`;
const FootCol = styled.div`
    p {
        margin: 0 0 0.35rem;
    }
    p:last-child {
        margin-bottom: 0;
    }
`;
const FootHead = styled.p`
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    font-size: 0.72rem;
    opacity: 0.6;
`;
/* The legal line. Doorstep Data is a brand, not a company - the operator is
   Property People London Ltd, and saying so is not optional: the Companies
   Act and the E-Commerce Regulations both require a trading name to
   identify the legal entity behind it, with its registration number, on the
   site itself. The agency footer carries the same wording. */
const Legal = styled.div`
    grid-column: 1 / -1;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
    padding-top: 1rem;
    font-size: 0.8rem;
    opacity: 0.75;
    line-height: 1.6;
`;

/**
 * The nav for whichever brand is being served.
 *
 * FORWARDS ITS PROPS TO THE AGENCY NAVBAR, which matters more than it
 * looks: NavBar renders a different set of links when location is "public",
 * so a caller that passed it and then switched to SiteNav would silently
 * start showing the signed-in admin nav to anybody with a session. That
 * happened to the 404 page on 29 Oct 2026 and was caught in the browser
 * rather than by a test.
 *
 * The product bar has no such modes - it is four links and a name - so the
 * props are simply unused on that side.
 */
export function SiteNav(props) {
    if (brandForHost(window.location.hostname).key !== "product") return <NavBar {...props} />;
    return (
        <Bar>
            <Inner>
                <Mark to="/">{BRANDS.product.name}</Mark>
                <Tag>{BRANDS.product.tagline}</Tag>
                <Nav>
                    <Link to="/property-report">Property report</Link>
                    <Link to="/house-prices">House prices</Link>
                    {/*
                      * The third, and it earns the slot rather than padding
                      * it out: until 5 November 2026 the only place a price
                      * appeared was the checkout, behind a sign-in and
                      * served noindex, so the one question somebody asks
                      * before making an account could only be answered
                      * after making one.
                      */}
                    <Link to="/pricing">Pricing</Link>
                    {/*
                      * THE ACCOUNT HAD NO LINK AT ALL UNTIL 4 NOVEMBER 2026.
                      * Sign-in, Google sign-in, magic links, device sign-in
                      * from the phone and both purchase screens were all
                      * built, deployed and reachable - and nothing anywhere
                      * on the site pointed at them. The only way in was to
                      * type the URL. Reported by somebody standing on the
                      * homepage asking where the sign-in was.
                      *
                      * The label follows the stored session rather than
                      * always saying "Sign in", because telling somebody who
                      * is already signed in to sign in reads as having been
                      * logged out. currentSession() is a localStorage read
                      * that never throws - see accountApi.js - so this costs
                      * no request and works in a private window.
                      */}
                    <Link to="/account">{currentSession() ? "Account" : "Sign in"}</Link>
                </Nav>
            </Inner>
        </Bar>
    );
}

export function SiteFooter() {
    if (brandForHost(window.location.hostname).key !== "product") return <Footer />;
    const year = new Date().getFullYear();
    return (
        <FootWrap>
            <FootInner>
                <FootCol>
                    <FootHead>{BRANDS.product.name}</FootHead>
                    <p>Public record data for one address, from official sources.</p>
                    <p>
                        <Link to="/property-report">Property report</Link> &middot;{" "}
                        <Link to="/house-prices">House prices</Link> &middot;{" "}
                        <Link to="/pricing">Pricing</Link> &middot;{" "}
                        <Link to="/account">Account</Link>
                    </p>
                    <p>
                        <Link to="/contact">Contact</Link> &middot;{" "}
                        <Link to="/privacy-policy">Privacy</Link>
                    </p>
                </FootCol>

                <FootCol>
                    <FootHead>Attribution</FootHead>
                    <p>
                        Contains HM Land Registry data &copy; Crown copyright and database right{" "}
                        {year}, and public sector information licensed under the Open Government
                        Licence v3.0.
                    </p>
                </FootCol>

                <FootCol>
                    <FootHead>Data protection</FootHead>
                    <p>
                        Registered with the Information Commissioner&rsquo;s Office, reference
                        ZA005338.
                    </p>
                </FootCol>

                <Legal>
                    &copy; {year} Property People London Ltd.
                    <br />
                    Doorstep Data is a trading name of Property People London Ltd (Reg No:
                    07355433)
                </Legal>
            </FootInner>
        </FootWrap>
    );
}
